import {
  BADGE_W,
  COLORS,
  FOOTER_H,
  HEADER_H,
  PAD,
  WIDTH,
  calcRowHeight,
  createCanvas,
  downloadCanvas,
  drawFooter,
  drawHeader,
  fill,
  fitText,
  loadLogo,
  roundRect,
  setFont,
  strokeBorder
} from './imageKit';

export type ShirtStockItem = {
  tamanho: string;
  necessario: number;
  estoque: number;
};

type GenerateOptions = {
  itens: ShirtStockItem[];
  ano: string;
};

const HERO_H = 110;
const NOTA_H = 38;
const LIST_TOP = 62;
const LIST_BOTTOM = 20;
const ICON = 26;

const drawCheck = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(x + 6, y + 13);
  ctx.lineTo(x + 11, y + 18);
  ctx.lineTo(x + 20, y + 8);
  ctx.stroke();
};

const drawCross = (ctx: CanvasRenderingContext2D, x: number, y: number) => {
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x + 8, y + 8);
  ctx.lineTo(x + 18, y + 18);
  ctx.moveTo(x + 18, y + 8);
  ctx.lineTo(x + 8, y + 18);
  ctx.stroke();
};

export const generateShirtStockImage = async ({ itens, ano }: GenerateOptions) => {
  const dados = itens.filter((item) => item.necessario > 0 || item.estoque > 0);
  if (dados.length === 0) return;

  const totalNecessario = dados.reduce((sum, item) => sum + item.necessario, 0);
  const totalEstoque = dados.reduce((sum, item) => sum + item.estoque, 0);
  const totalFaltando = dados.reduce((sum, item) => sum + Math.max(0, item.necessario - item.estoque), 0);
  const totalSobrando = dados.reduce((sum, item) => sum + Math.max(0, item.estoque - item.necessario), 0);

  // Faltas e sobras são contadas por tamanho: sobra de um tamanho não cobre a falta de outro.
  const nota =
    totalFaltando > 0 && totalSobrando > 0
      ? `Faltam ${totalFaltando} em alguns tamanhos e sobram ${totalSobrando} em outros — as sobras não cobrem as faltas.`
      : totalFaltando > 0
      ? `Faltam ${totalFaltando} camisas para atender todas as inscrições.`
      : totalSobrando > 0
      ? `Estoque suficiente em todos os tamanhos — sobram ${totalSobrando} camisas.`
      : 'Estoque exato para todas as inscrições.';

  const alturaFixa = HEADER_H + 32 + HERO_H + NOTA_H + 24 + LIST_TOP + LIST_BOTTOM + 24 + FOOTER_H;
  const rowH = calcRowHeight(dados.length, alturaFixa);
  const listaH = LIST_TOP + dados.length * rowH + LIST_BOTTOM;
  const height = alturaFixa + dados.length * rowH;

  const logo = await loadLogo();
  const surface = createCanvas(height);
  if (!surface) return;
  const { canvas, ctx } = surface;

  drawHeader(ctx, logo, 'Camisas: Necessário x Estoque', `Período ${ano}`);

  // Indicadores
  const heroY = HEADER_H + 32;
  const heroGap = 16;
  const heroW = (WIDTH - PAD * 2 - heroGap * 3) / 4;
  const indicadores = [
    { label: 'NECESSÁRIO', valor: String(totalNecessario), cor: COLORS.title },
    { label: 'ENCOMENDADO', valor: String(totalEstoque), cor: COLORS.primary },
    { label: 'FALTAM', valor: String(totalFaltando), cor: totalFaltando > 0 ? COLORS.red : COLORS.green },
    { label: 'SOBRAM', valor: String(totalSobrando), cor: totalSobrando > 0 ? COLORS.green : COLORS.muted }
  ];

  indicadores.forEach((item, index) => {
    const x = PAD + index * (heroW + heroGap);
    roundRect(ctx, x, heroY, heroW, HERO_H, 12);
    fill(ctx, COLORS.cardBg);
    strokeBorder(ctx, COLORS.border);
    ctx.textAlign = 'center';
    ctx.fillStyle = COLORS.muted;
    const label = fitText(ctx, item.label, heroW - 16, 700, 12, 9);
    ctx.fillText(label, x + heroW / 2, heroY + 36);
    ctx.fillStyle = item.cor;
    setFont(ctx, 800, 40);
    ctx.fillText(item.valor, x + heroW / 2, heroY + 82);
    ctx.textAlign = 'left';
  });

  // Nota: explica por que necessário - encomendado não é igual ao que falta.
  ctx.textAlign = 'center';
  ctx.fillStyle = COLORS.text;
  const notaAjustada = fitText(ctx, nota, WIDTH - PAD * 2 - 24, 600, 15, 11);
  ctx.fillText(notaAjustada, WIDTH / 2, heroY + HERO_H + 26);
  ctx.textAlign = 'left';

  // Tabela
  const listaY = heroY + HERO_H + NOTA_H + 24;
  roundRect(ctx, PAD, listaY, WIDTH - PAD * 2, listaH, 12);
  fill(ctx, COLORS.cardBg);
  strokeBorder(ctx, COLORS.border);

  const x0 = PAD + 24;
  const innerW = WIDTH - PAD * 2 - 48;
  const colNecessario = x0 + 330;
  const colEstoque = x0 + 490;

  ctx.fillStyle = COLORS.muted;
  setFont(ctx, 700, 12);
  ctx.fillText('TAMANHO', x0, listaY + 34);
  ctx.textAlign = 'right';
  ctx.fillText('NECESSÁRIO', colNecessario, listaY + 34);
  ctx.fillText('ESTOQUE', colEstoque, listaY + 34);
  ctx.fillText('FALTA / SOBRA', x0 + innerW, listaY + 34);
  ctx.textAlign = 'left';

  ctx.strokeStyle = COLORS.border;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0, listaY + 48);
  ctx.lineTo(x0 + innerW, listaY + 48);
  ctx.stroke();

  dados.forEach((item, index) => {
    const rowY = listaY + LIST_TOP + index * rowH;
    const center = rowY + rowH / 2;
    const saldo = item.estoque - item.necessario;
    const falta = saldo < 0;

    if (index % 2 === 0) {
      roundRect(ctx, PAD + 12, rowY + 3, WIDTH - PAD * 2 - 24, rowH - 6, 8);
      fill(ctx, COLORS.softBg);
    }

    // Etiqueta do tamanho
    roundRect(ctx, x0, center - 24, BADGE_W, 48, 10);
    fill(ctx, COLORS.badgeBg);
    const rotulo = fitText(ctx, item.tamanho, BADGE_W - 16, 800, 26, 11);
    ctx.fillStyle = COLORS.primaryDark;
    ctx.textAlign = 'center';
    ctx.fillText(rotulo, x0 + BADGE_W / 2, center + 9);
    ctx.textAlign = 'left';

    // Necessário e estoque
    ctx.textAlign = 'right';
    ctx.fillStyle = COLORS.title;
    setFont(ctx, 800, 30);
    ctx.fillText(String(item.necessario), colNecessario, center + 10);
    ctx.fillStyle = COLORS.text;
    setFont(ctx, 700, 30);
    ctx.fillText(String(item.estoque), colEstoque, center + 10);
    ctx.textAlign = 'left';

    // Situação
    const texto = saldo > 0 ? `Sobram ${saldo}` : saldo === 0 ? 'Certo' : `Faltam ${Math.abs(saldo)}`;
    const cor = falta ? COLORS.red : COLORS.green;
    setFont(ctx, 700, 16);
    const textoW = ctx.measureText(texto).width;
    const grupoX = x0 + innerW - (ICON + 10 + textoW);

    roundRect(ctx, grupoX, center - ICON / 2, ICON, ICON, 7);
    fill(ctx, cor);
    if (falta) drawCross(ctx, grupoX, center - ICON / 2);
    else drawCheck(ctx, grupoX, center - ICON / 2);

    ctx.fillStyle = cor;
    ctx.fillText(texto, grupoX + ICON + 10, center + 6);
  });

  drawFooter(ctx, listaY + listaH + 24, `Faltam ${totalFaltando} • Sobram ${totalSobrando}`);
  downloadCanvas(canvas, `comparativo-camisas-${ano}.png`);
};
